import i18next from 'i18next';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  fetchActiveTreatmentPlan,
  fetchExerciseCompliance,
  fetchTreatmentPlanVersion,
  fetchTreatmentPlanVersions,
  saveTreatmentPlan,
  shareTreatmentPlan,
  unshareTreatmentPlan,
} from '@/api/treatmentPlans/treatmentPlansApi';
import type {
  ExerciseCompliance,
  TreatmentPlan,
  TreatmentPlanVersionSummary,
  TreatmentPlanWritePayload,
} from '@/api/treatmentPlans/treatmentPlansApi.types';
import {
  buildExerciseComplianceQueryKey,
  buildPatientTimelineQueryKey,
  buildPatientTreatmentPlanQueryKey,
  buildPatientTreatmentPlanVersionsQueryKey,
  buildTreatmentPlanVersionQueryKey,
} from '@/constants/queryKeys';

export function usePatientTreatmentPlanQuery(
  patientId: string
): UseQueryResult<TreatmentPlan | null, Error> {
  return useQuery({
    queryKey: [...buildPatientTreatmentPlanQueryKey(patientId), i18next.language],
    queryFn: function loadPlan() {
      return fetchActiveTreatmentPlan(patientId);
    },
    enabled: patientId.length > 0,
  });
}

export function usePatientTreatmentPlanVersionsQuery(
  patientId: string
): UseQueryResult<TreatmentPlanVersionSummary[], Error> {
  return useQuery({
    queryKey: buildPatientTreatmentPlanVersionsQueryKey(patientId),
    queryFn: function loadVersions() {
      return fetchTreatmentPlanVersions(patientId);
    },
    enabled: patientId.length > 0,
  });
}

export function useTreatmentPlanVersionQuery(
  planId: string,
  enabled: boolean
): UseQueryResult<TreatmentPlan, Error> {
  return useQuery({
    queryKey: [...buildTreatmentPlanVersionQueryKey(planId), i18next.language],
    queryFn: function loadVersion() {
      return fetchTreatmentPlanVersion(planId);
    },
    enabled: enabled && planId.length > 0,
  });
}

function useTreatmentPlanInvalidation() {
  const queryClient = useQueryClient();
  return function invalidate(plan: TreatmentPlan) {
    queryClient.invalidateQueries({
      queryKey: buildPatientTreatmentPlanQueryKey(plan.patientId),
    });
    queryClient.invalidateQueries({
      queryKey: buildPatientTreatmentPlanVersionsQueryKey(plan.patientId),
    });
    queryClient.invalidateQueries({ queryKey: buildTreatmentPlanVersionQueryKey(plan.id) });
    queryClient.invalidateQueries({ queryKey: buildPatientTimelineQueryKey(plan.patientId) });
    queryClient.invalidateQueries({ queryKey: buildExerciseComplianceQueryKey(plan.patientId) });
  };
}

export function usePatientExerciseComplianceQuery(
  patientId: string
): UseQueryResult<ExerciseCompliance[], Error> {
  return useQuery({
    queryKey: [...buildExerciseComplianceQueryKey(patientId), i18next.language],
    queryFn: function loadCompliance() {
      return fetchExerciseCompliance(patientId);
    },
    enabled: patientId.length > 0,
  });
}

interface SaveTreatmentPlanVariables {
  patientId: string;
  payload: TreatmentPlanWritePayload;
}

export function useSaveTreatmentPlanMutation(): UseMutationResult<
  TreatmentPlan,
  Error,
  SaveTreatmentPlanVariables
> {
  const invalidate = useTreatmentPlanInvalidation();
  return useMutation({
    mutationFn: function save(variables: SaveTreatmentPlanVariables) {
      return saveTreatmentPlan(variables.patientId, variables.payload);
    },
    onSuccess: invalidate,
  });
}

export function useShareTreatmentPlanMutation(): UseMutationResult<TreatmentPlan, Error, string> {
  const invalidate = useTreatmentPlanInvalidation();
  return useMutation({ mutationFn: shareTreatmentPlan, onSuccess: invalidate });
}

export function useUnshareTreatmentPlanMutation(): UseMutationResult<
  TreatmentPlan,
  Error,
  string
> {
  const invalidate = useTreatmentPlanInvalidation();
  return useMutation({ mutationFn: unshareTreatmentPlan, onSuccess: invalidate });
}
